import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "@/components/brand/Logo";
import { NAV } from "@/constants/testIds";
import { CONTACT_INFO } from "@/constants/contact";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from "@/components/ui/sheet";

const NAV_ITEMS = [
  { label: "Home", to: "/", testId: NAV.linkHome },
  { label: "About Us", to: "/about", testId: NAV.linkAbout },
  { label: "Services", to: "/services", testId: NAV.linkServices },
  { label: "Contact Us", to: "/contact", testId: NAV.linkContact },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      className={`sticky top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md shadow-[0_2px_18px_-6px_rgba(23,32,51,0.12)] border-b border-brand-border"
          : "bg-white/95 backdrop-blur-sm border-b border-brand-border/60"
      }`}
    >
      <div className="container-brand flex h-[72px] md:h-[84px] items-center justify-between gap-4">
        <Link
          to="/"
          data-testid={NAV.logo}
          className="flex items-center gap-3 group shrink-0"
          aria-label="Ascend Tech Solutions home"
        >
          <Logo className="h-11 md:h-14 w-auto transition-transform group-hover:scale-[1.03]" />
          <div className="leading-tight">
            <div className="font-heading font-extrabold text-brand-navy text-[15px] md:text-lg tracking-tight uppercase">
              Ascend Tech
            </div>
            <div className="font-heading font-extrabold text-brand-royal text-[15px] md:text-lg tracking-tight uppercase -mt-0.5">
              Solutions
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={item.testId}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-semibold transition-colors ${
                  isActive ? "text-brand-royal" : "text-brand-text hover:text-brand-royal"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span>{item.label}</span>
                  <span
                    className={`absolute left-4 right-4 -bottom-0.5 h-[2px] rounded-full transition-all ${
                      isActive ? "bg-brand-teal opacity-100" : "opacity-0"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/contact?type=employer"
            data-testid={NAV.hireCta}
            className="btn-primary text-sm"
          >
            Hire Talent
          </Link>
        </div>

        <div className="lg:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                aria-label="Open navigation menu"
                data-testid={NAV.mobileToggle}
                className="inline-flex items-center justify-center rounded-md p-2 text-brand-navy hover:bg-brand-soft focus:outline-none focus:ring-2 focus:ring-brand-royal"
              >
                {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-[88vw] sm:w-[380px] bg-white p-0"
              data-testid={NAV.mobileMenu}
            >
              <SheetTitle className="sr-only">Site Navigation</SheetTitle>
              <div className="flex items-center gap-3 px-6 py-5 border-b border-brand-border">
                <Logo className="h-11 w-auto" />
                <div className="leading-tight">
                  <div className="font-heading font-extrabold text-brand-navy text-sm uppercase tracking-tight">Ascend Tech</div>
                  <div className="font-heading font-extrabold text-brand-royal text-sm uppercase tracking-tight -mt-0.5">Solutions</div>
                </div>
              </div>
              <nav className="flex flex-col p-3">
                {NAV_ITEMS.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === "/"}
                    data-testid={`${item.testId}-mobile`}
                    className={({ isActive }) =>
                      `px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                        isActive ? "bg-brand-soft text-brand-royal" : "text-brand-text hover:bg-brand-soft"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
              </nav>
              <div className="p-5 border-t border-brand-border mt-2">
                <Link to="/contact?type=employer" className="btn-primary w-full">Hire Talent</Link>
                <a href={CONTACT_INFO.callHrefTel} className="mt-3 block text-center text-sm text-brand-textSoft">
                  Call: {CONTACT_INFO.callNumberDisplay}
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
